import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { tagChipStyle } from "@/lib/tagColor";
import { toast } from "sonner";
import { Save, Plus, Trash2, Link as LinkIcon, Upload, Star, FileText, Pencil, Check, X, Wrench, Package, Archive } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PASTEL_COLORS, SWATCH_DEFAULT } from "@/lib/palette";
import { RichMarkdownEditor } from "@/components/RichMarkdownEditor";
import { useConfirm } from "@/context/ConfirmContext";
import ImportWizard from "@/components/ImportWizard";
import { Users, CalendarClock, Download, Keyboard, RotateCcw, AlertTriangle, Database, Settings2, UploadCloud } from "lucide-react";
import { SHORTCUT_GROUPS, FIXED_SHORTCUTS, DEFAULTS, loadShortcuts, saveShortcutsLocal, SHORTCUT_DEFS, comboLabel, isReserved, sameCombo } from "@/lib/shortcuts";
import { CARE_TYPE_TONES, toneForType, firstFreeToneKey, usedToneKeys } from "@/lib/careTones";

const PASTELS = PASTEL_COLORS;
const randomPastel = () => PASTELS[Math.floor(Math.random() * PASTELS.length)];
const CAT_COLORS = ["#141821", "#3b82f6", "#facc15", "#22c55e", "#8b5cf6", "#f59e0b"];

const GROUPS = [
  ["CABINET", [["ps", "Information cabinet"], ["agenda", "Agenda & créneaux"], ["connexions", "Intégrations"], ["compte", "Compte utilisateur"]]],
  ["PATIENTS", [["tags", "Tags patients"], ["secteurs", "Secteurs"]]],
  ["SOINS", [["types", "Types de soin"], ["trames", "Trames de soin"], ["soinfiche", "Fiche de soin"]]],
  ["COMMUNICATION", [["messages", "Modèles de messages"]]],
  ["FACTURATION", [["catalogue", "Catalogue"], ["categories", "Catégories"], ["facture", "Modèle facture"]]],
  ["RACCOURCIS", [["raccourcis", "Raccourcis clavier"]]],
  ["DONNÉES", [["data", "Importer"], ["export", "Exporter"]]],
];
const GROUP_OF = {};
GROUPS.forEach(([g, items]) => items.forEach(([s, l]) => { GROUP_OF[s] = { group: g, label: l }; }));

export default function Parametres() {
  const { section } = useParams();
  const nav = useNavigate();
  const sec = GROUP_OF[section] ? section : "ps";
  const meta = GROUP_OF[sec];
  const groupLabel = meta.group.charAt(0) + meta.group.slice(1).toLowerCase();
  return (
    <div>
      <div className="mb-5">
        <div className="text-[12px]" style={{ color: "var(--muted-2)" }}>Paramètres › {groupLabel}</div>
        <h1 className="text-[24px] font-bold tracking-[-0.01em] mt-0.5" style={{ color: "var(--ink)" }}>
          <span style={{ background: "linear-gradient(transparent 62%, var(--accent) 62%)", padding: "0 2px" }}>{meta.label}</span>
        </h1>
      </div>
      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        <nav className="h-fit flex flex-col gap-3.5">
          {GROUPS.map(([g, items]) => (
            <div key={g}>
              <div className="text-[11px] font-semibold uppercase px-2.5 pb-1" style={{ color: "var(--muted-2)", letterSpacing: ".06em" }}>{g}</div>
              {items.map(([s, l]) => (
                <button key={s} data-testid={`settings-panel-${s}`} onClick={() => nav(`/parametres/${s}`)}
                  className="w-full text-left text-[13px] rounded-md px-2.5 py-1.5 mb-0.5 transition-colors"
                  style={sec === s ? { background: "#fff", color: "var(--ink)", fontWeight: 600, boxShadow: "0 1px 2px rgba(0,0,0,.06)" } : { color: "#4a5060" }}>{l}</button>
              ))}
            </div>
          ))}
        </nav>
        <div>
          {sec === "ps" && <PsPanel />}
          {sec === "agenda" && <AgendaPanel />}
          {sec === "connexions" && <ConnexionsPanel />}
          {sec === "compte" && <ComptePanel />}
          {sec === "secteurs" && <SecteurPanel />}
          {sec === "messages" && <ListPanel title="Modèles de messages" endpoint="/settings/message-templates" fields={[["nom", "Nom"], ["contenu", "Contenu"]]} render={(x) => `${x.nom} (${x.canal})`} testid="message" extra={{ canal: "email", categorie: "annonce" }} />}
          {sec === "types" && <CareTypePanel />}
          {sec === "tags" && <TagsPanel />}
          {sec === "trames" && <TramePanel />}
          {sec === "soinfiche" && <FicheSoinPanel />}
          {sec === "catalogue" && <CataloguePanel />}
          {sec === "categories" && <CategoriesPanel />}
          {sec === "facture" && <FacturePanel />}
          {sec === "raccourcis" && <ShortcutsPanel />}
          {sec === "data" && <DataPanel />}
          {sec === "export" && <ExportPanel />}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return <div className="bg-white sx-card p-5"><h3 className="font-display font-semibold text-slate-900 mb-4">{title}</h3>{children}</div>;
}
const Field = ({ label, ...p }) => (
  <div className="mb-3"><label className="text-sm font-medium text-slate-700">{label}</label><input {...p} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" /></div>
);

function PsPanel() {
  const [s, setS] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const fileRef = useRef();
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/settings/logo").then((r) => setLogoPreview(r.data.data_url)).catch(() => {});
  }, []);
  const save = async () => { try { await api.put("/settings", s); toast.success("Enregistré"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const logo = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setLogoPreview(URL.createObjectURL(f));
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setS((prev) => ({ ...prev, ps_logo_url: data.ps_logo_url })); toast.success("Logo mis à jour"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const set = (k, v) => setS({ ...s, [k]: v });
  return (
    <Section title="Informations du praticien">
      <div className="grid sm:grid-cols-2 gap-x-4">
        <Field label="Nom professionnel" data-testid="ps-nom" value={s.ps_nom || ""} onChange={(e) => set("ps_nom", e.target.value)} />
        <Field label="Téléphone" value={s.ps_telephone || ""} onChange={(e) => set("ps_telephone", e.target.value)} />
        <Field label="Email" value={s.ps_email || ""} onChange={(e) => set("ps_email", e.target.value)} />
        <Field label="Adresse" value={s.ps_adresse || ""} onChange={(e) => set("ps_adresse", e.target.value)} />
        <Field label="SIRET" value={s.ps_siret || ""} onChange={(e) => set("ps_siret", e.target.value)} />
        <Field label="ADELI / RPPS" value={s.ps_adeli || ""} onChange={(e) => set("ps_adeli", e.target.value)} />
        <Field label="Site web" value={s.site_web || ""} onChange={(e) => set("site_web", e.target.value)} />
        <Field label="Instagram" value={s.instagram || ""} onChange={(e) => set("instagram", e.target.value)} />
      </div>
      <div className="mb-3"><label className="text-sm font-medium text-slate-700">Signature email</label><textarea rows={2} value={s.signature_email || ""} onChange={(e) => set("signature_email", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" /></div>
      <div className="border-t border-slate-100 pt-3 mb-3">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Logo</div>
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
            {logoPreview ? <img src={logoPreview} alt="Logo" data-testid="ps-logo-preview" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-slate-400 px-2 text-center">Aucun logo</span>}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={logo} />
            <button type="button" data-testid="ps-logo-btn" onClick={() => fileRef.current.click()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"><Upload className="w-4 h-4" />Choisir un logo</button>
            <p className="text-xs text-slate-400 mt-2">PNG ou JPG — aperçu immédiat.</p>
          </div>
        </div>
      </div>
      <button data-testid="ps-save" onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
    </Section>
  );
}

function FicheSoinPanel() {
  const [s, setS] = useState({});
  const [careTypes, setCareTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/settings/care-types").then((r) => setCareTypes(r.data)).catch(() => {});
    api.get("/settings/soin-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);
  const DEF = PASTEL_COLORS.slice(0, 6);
  const cols = s.anatomie_colors && s.anatomie_colors.length ? s.anatomie_colors : DEF;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => { await api.put("/settings", { anatomie_colors: cols, care_type_defaut: s.care_type_defaut || "", trame_defaut: s.trame_defaut || "" }); toast.success("Enregistré"); };
  return (
    <Section title="Fiche de soin">
      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="text-sm font-medium text-slate-700">Type de soin par défaut</label>
          <select data-testid="soinfiche-default-type" value={s.care_type_defaut || ""} onChange={(e) => set("care_type_defaut", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm">
            <option value="">— Aucun —</option>
            {careTypes.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
          <p className="text-[11px] text-slate-400 mt-1">Présélectionné à la création d'un RDV ou d'un soin.</p>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Trame de soin par défaut</label>
          <select data-testid="soinfiche-default-trame" value={s.trame_defaut || ""} onChange={(e) => set("trame_defaut", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm">
            <option value="">— Aucune —</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.nom}</option>)}
          </select>
          <p className="text-[11px] text-slate-400 mt-1">Insérée automatiquement dans les nouveaux soins.</p>
        </div>
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Couleurs des notes d'anatomie (post-it pastel)</div>
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {cols.map((c, i) => (
          <span key={i} className="flex items-center gap-1">
            <input type="color" data-testid={`anat-color-${i}`} value={c} onChange={(e) => { const arr = [...cols]; arr[i] = e.target.value; set("anatomie_colors", arr); }} className="w-8 h-8 rounded border border-slate-200 cursor-pointer" />
            <button onClick={() => { const arr = [...cols]; arr.splice(i, 1); set("anatomie_colors", arr); }} className="text-slate-300 hover:text-red-500 text-xs">✕</button>
          </span>
        ))}
        <button data-testid="anat-color-add" onClick={() => set("anatomie_colors", [...cols, SWATCH_DEFAULT])} className="px-2 py-1 rounded-lg border border-slate-200 text-xs">+ Couleur</button>
      </div>
      <button data-testid="soinfiche-save" onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
    </Section>
  );
}

function AgendaPanel() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const save = async () => { await api.put("/settings", s); toast.success("Enregistré"); };
  const set = (k, v) => setS({ ...s, [k]: v });
  const pasVal = s.pas_creneaux || 30;
  const savePas = async (v) => { setS((p) => ({ ...p, pas_creneaux: v })); try { await api.put("/settings", { pas_creneaux: v }); toast.success("Enregistré automatiquement · à l'instant"); } catch { toast.error("Échec · Réessayer"); } };
  const preview = (() => { const out = []; let m = 9 * 60; for (let i = 0; i < 6; i++) { out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`); m += pasVal; } return out; })();
  return (
    <Section title="Agenda & créneaux">
      <div className="mb-6">
        <div className="text-sm font-medium text-slate-700 mb-1.5">Pas des créneaux</div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="pas-segment">
          {[15, 30, 60].map((v) => (
            <button key={v} data-testid={`pas-${v}`} onClick={() => savePas(v)} className={`px-4 py-2 text-sm transition-colors ${pasVal === v ? "bg-[#141821] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{v === 60 ? "1 h" : `${v} min`}</button>
          ))}
        </div>
        <p className="text-[12.5px] text-slate-500 mt-1.5">Utilisé pour proposer les heures lors de la prise de RDV et pour aimanter les déplacements dans l'agenda.</p>
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="pas-preview">
          {preview.map((t) => <span key={t} className="mono text-[11px] px-2 py-1 rounded" style={{ background: "var(--bg-warm)", color: "var(--text-2)" }}>{t}</span>)}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-x-4">
        <Field label="Durée soin par défaut (min)" type="number" value={s.duree_soin_defaut || 60} onChange={(e) => set("duree_soin_defaut", Number(e.target.value))} />
        <Field label="Temps avant (min)" type="number" value={s.temps_avant || 0} onChange={(e) => set("temps_avant", Number(e.target.value))} />
        <Field label="Temps après (min)" type="number" value={s.temps_apres || 15} onChange={(e) => set("temps_apres", Number(e.target.value))} />
      </div>
      <button data-testid="agenda-settings-save" onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
    </Section>
  );
}

function ComptePanel() {
  const [email, setEmail] = useState("");
  const [tel, setTel] = useState("");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [instanceId, setInstanceId] = useState("");
  const [cur, setCur] = useState("");
  const [np1, setNp1] = useState("");
  const [np2, setNp2] = useState("");
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";
  useEffect(() => { api.get("/auth/me").then((r) => { setEmail(r.data.email || ""); setTel(r.data.telephone || ""); setNom(r.data.nom || ""); setPrenom(r.data.prenom || ""); }); api.get("/auth/instance").then((r) => setInstanceId(r.data.instance_id || "")).catch(() => {}); }, []);
  const saveAccount = async () => {
    try { await api.put("/auth/account", { email, telephone: tel, nom, prenom }); toast.success("Compte mis à jour"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changePwd = async () => {
    if (np1.length < 8) { toast.error("Le mot de passe doit contenir au moins 8 caractères"); return; }
    if (np1 !== np2) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    try { await api.post("/auth/change-password", { current_password: cur, new_password: np1, app_url: appUrl }); toast.success("Mot de passe modifié"); setCur(""); setNp1(""); setNp2(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <Section title="Instance d'application">
        <div className="mb-3">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">URL d'accès à l'application</div>
          <a href={appUrl} target="_blank" rel="noopener noreferrer" data-testid="app-url" className="text-sm font-medium text-emerald-700 hover:underline break-all">{appUrl}</a>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">ID d'instance</div>
          <code data-testid="instance-id" className="text-sm font-mono text-slate-700 bg-slate-100 rounded px-2 py-1 break-all">{instanceId || "—"}</code>
        </div>
      </Section>
      <Section title="Compte utilisateur">
        <p className="text-xs text-slate-400 mb-3">Identifiants de connexion à l'application. Le téléphone n'est pas utilisé pour la connexion ; il est conservé pour une récupération éventuelle.</p>
        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label="Nom" data-testid="account-nom" value={nom} onChange={(e) => setNom(e.target.value)} />
          <Field label="Prénom" data-testid="account-prenom" value={prenom} onChange={(e) => setPrenom(e.target.value)} />
          <Field label="Email de connexion" type="email" data-testid="account-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field label="Téléphone associé au compte (récupération)" data-testid="account-phone" placeholder="+33…" value={tel} onChange={(e) => setTel(e.target.value)} />
        </div>
        <button data-testid="account-save" onClick={saveAccount} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
      </Section>
      <Section title="Changer le mot de passe">
        <Field label="Mot de passe actuel" type="password" data-testid="account-current-pwd" value={cur} onChange={(e) => setCur(e.target.value)} />
        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label="Nouveau mot de passe" type="password" data-testid="account-new-pwd" value={np1} onChange={(e) => setNp1(e.target.value)} />
          <Field label="Confirmer le nouveau mot de passe" type="password" data-testid="account-new-pwd2" value={np2} onChange={(e) => setNp2(e.target.value)} />
        </div>
        <p className="text-xs text-slate-400 mb-3">8 caractères minimum.</p>
        <button data-testid="account-change-pwd" onClick={changePwd} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Modifier le mot de passe</button>
      </Section>
    </div>
  );
}

function ConnexionsPanel() {
  const [status, setStatus] = useState(null);
  const [cals, setCals] = useState([]);
  const [s, setS] = useState({});
  const load = () => { api.get("/calendar/status").then((r) => setStatus(r.data)); api.get("/settings").then((r) => setS(r.data)); };
  useEffect(load, []);
  const set = (k, v) => setS({ ...s, [k]: v });
  const saveTwilio = async () => { await api.put("/settings", { twilio_account_sid: s.twilio_account_sid || "", twilio_auth_token: s.twilio_auth_token || "", twilio_from: s.twilio_from || "" }); toast.success("Twilio enregistré"); };
  const [twilioTestTo, setTwilioTestTo] = useState("");
  useEffect(() => { if (s.sms_test_to && !twilioTestTo) setTwilioTestTo(s.sms_test_to); }, [s.sms_test_to]);
  const testTwilio = async () => {
    if (!twilioTestTo.trim()) { toast.error("Saisissez un numéro de test"); return; }
    await api.put("/settings", { twilio_account_sid: s.twilio_account_sid || "", twilio_auth_token: s.twilio_auth_token || "", twilio_from: s.twilio_from || "", sms_test_to: twilioTestTo.trim() });
    try { const { data } = await api.post("/settings/sms-test", { to: twilioTestTo.trim() }); data.ok ? toast.success(data.message) : toast.error(data.message); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test impossible"); }
  };
  const saveAI = async () => { await api.put("/settings", { ai_provider: s.ai_provider || "openai", openai_api_key: s.openai_api_key || "", anthropic_api_key: s.anthropic_api_key || "" }); toast.success("Intelligence artificielle enregistrée"); };
  const saveStripe = async () => { await api.put("/settings", { stripe_mode: s.stripe_mode || "test", stripe_publishable_key_test: s.stripe_publishable_key_test || "", stripe_secret_key_test: s.stripe_secret_key_test || "", stripe_publishable_key_live: s.stripe_publishable_key_live || "", stripe_secret_key_live: s.stripe_secret_key_live || "" }); toast.success("Stripe enregistré"); };
  const verify = async (service) => { try { const { data } = await api.post("/settings/verify", { service }); data.ok ? toast.success(data.message) : toast.error(data.message); } catch (e) { toast.error("Vérification impossible"); } };
  const [emailLists, setEmailLists] = useState([]);
  const loadEmailLists = async () => { try { const { data } = await api.get("/settings/email-lists"); setEmailLists(data); toast.success(`${data.length} liste(s) chargée(s)`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Connexion impossible"); } };
  useEffect(() => {
    if (!s.email_provider || !s.email_api_key || !s.email_list_id) return;
    if (emailLists.length) return;
    api.get("/settings/email-lists").then(({ data }) => {
      setEmailLists(data);
      const match = data.find((l) => String(l.id) === String(s.email_list_id));
      if (match && match.name !== s.email_list_name) setS((prev) => ({ ...prev, email_list_name: match.name }));
    }).catch(() => {});
  }, [s.email_provider, s.email_api_key, s.email_list_id]);
  const saveEmail = async () => { await api.put("/settings", { email_provider: s.email_provider || "", email_api_key: s.email_api_key || "", email_list_id: s.email_list_id || "", email_list_name: s.email_list_name || "" }); toast.success("Emailing enregistré"); };
  const saveSmtp = async () => { await api.put("/settings", { smtp_enabled: !!s.smtp_enabled, smtp_host: s.smtp_host || "", smtp_port: Number(s.smtp_port) || 587, smtp_username: s.smtp_username || "", smtp_password: s.smtp_password || "", smtp_from: s.smtp_from || "", smtp_security: s.smtp_security || "starttls", smtp_test_to: s.smtp_test_to || "" }); toast.success("SMTP enregistré"); };
  const testSmtp = async () => {
    await saveSmtp();
    const to = (s.smtp_test_to || s.smtp_from || "").trim();
    if (!to) { toast.error("Renseignez un email de test"); return; }
    try { const { data } = await api.post("/settings/smtp-test", { to }); data.ok ? toast.success(data.message) : toast.error(data.message); }
    catch (e) { toast.error("Test impossible"); }
  };
  const connect = async () => {
    try { const { data } = await api.get("/oauth/calendar/login"); window.location.href = data.authorization_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disconnect = async () => { await api.post("/oauth/calendar/disconnect"); setCals([]); toast.success("Compte Google déconnecté"); load(); };
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("google");
    if (p === "connected") toast.success("Google Agenda connecté");
    else if (p === "error") toast.error("Connexion Google annulée ou échouée");
    if (p) window.history.replaceState({}, "", window.location.pathname);
  }, []);
  const loadCals = async () => { try { const { data } = await api.get("/oauth/calendar/calendars"); setCals(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  // Charge automatiquement la liste des agendas dès que le compte est connecté
  useEffect(() => { if (status?.connected && cals.length === 0) loadCals(); }, [status?.connected]);
  const selectCal = async (c) => {
    await api.put("/settings", { google_calendar_id: c.id, google_calendar_name: c.summary });
    setS((prev) => ({ ...prev, google_calendar_id: c.id, google_calendar_name: c.summary }));
    toast.success(`Agenda par défaut : ${c.summary}`);
  };
  return (
    <div className="space-y-4">
      <Section title="Intelligence artificielle (dictée & IA)">
        <p className="text-xs text-slate-400 mb-3">La transcription des notes vocales utilise <span className="font-medium">OpenAI Whisper</span> avec votre clé OpenAI. Choisissez aussi le moteur IA (fonctions à venir).</p>
        <p className="text-xs text-slate-400 mb-3">Pour suivre &amp; créditer OpenAI : <a href="https://platform.openai.com/settings/organization/billing/overview" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">https://platform.openai.com/settings/organization/billing/overview</a></p>
        <div className="mb-3">
          <label className="text-sm font-medium text-slate-700">Moteur IA préféré</label>
          <select data-testid="ai-provider" value={s.ai_provider || "openai"} onChange={(e) => set("ai_provider", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm">
            <option value="openai">OpenAI (ChatGPT)</option>
            <option value="anthropic">Anthropic (Claude)</option>
          </select>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label="Clé API OpenAI (requise pour la dictée/vocal)" type="password" data-testid="openai-key" value={s.openai_api_key || ""} onChange={(e) => set("openai_api_key", e.target.value)} />
          <Field label="Clé API Anthropic (Claude)" type="password" data-testid="anthropic-key" value={s.anthropic_api_key || ""} onChange={(e) => set("anthropic_api_key", e.target.value)} />
        </div>
        <button data-testid="ai-save" onClick={saveAI} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
        <button data-testid="ai-verify" onClick={async () => { await saveAI(); verify(s.ai_provider === "anthropic" ? "anthropic" : "openai"); }} className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Vérifier</button>
      </Section>
      <Section title="Google Agenda">
        <p className="text-xs text-slate-400 mb-3">Connectez votre agenda Google en un clic : vous serez redirigé vers Google pour autoriser l'accès, puis ramené ici. Aucune clé à saisir.</p>
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">Statut : <span className="font-medium" data-testid="google-status">{status?.connected ? "Connecté" : status?.configured ? "Non connecté" : "Indisponible sur ce serveur"}</span></div>
          <div className="flex items-center gap-2">
            {status?.connected && <button data-testid="google-disconnect" onClick={disconnect} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">Déconnecter</button>}
            {status?.connected
              ? <button data-testid="google-choose-cal" onClick={loadCals} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">Rafraîchir la liste</button>
              : <button data-testid="google-connect" disabled={!status?.configured} onClick={connect} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium disabled:opacity-50"><LinkIcon className="w-4 h-4" />Se connecter avec Google</button>}
          </div>
        </div>
        {status?.connected && (
          <div className="mt-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Agenda synchronisé par défaut</div>
            <div data-testid="google-default-cal" className="text-sm text-slate-700 mb-2">{s.google_calendar_name || (s.google_calendar_id === "primary" || !s.google_calendar_id ? "Agenda principal" : s.google_calendar_id)}</div>
            {cals.length > 0 && <div className="space-y-1 border-t border-slate-100 pt-2" data-testid="google-cal-list">{cals.map((c) => {
              const selected = (s.google_calendar_id || "primary") === c.id;
              return <div key={c.id} className="text-sm flex items-center justify-between py-0.5">
                <span className={selected ? "font-medium text-slate-900" : "text-slate-600"}>{c.summary}</span>
                {selected
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Par défaut</span>
                  : <button data-testid={`google-cal-select-${c.id}`} onClick={() => selectCal(c)} className="text-emerald-600 text-xs hover:underline">Définir par défaut</button>}
              </div>;
            })}</div>}
          </div>
        )}
      </Section>
      <Section title="Prestataire de paiement (Stripe)">
        <div className="mb-3">
          <label className="text-sm font-medium text-slate-700">Mode</label>
          <div className="sx-seg mt-1 inline-flex">
            {[["test", "Test"], ["live", "Live (production)"]].map(([k, l]) => (
              <button key={k} data-testid={`stripe-mode-${k}`} onClick={() => set("stripe_mode", k)} className="sx-seg-item" data-active={(s.stripe_mode || "test") === k}>{l}</button>
            ))}
          </div>
        </div>
        <div className="mb-2">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Clés Test</div>
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Clé publique test (pk_test_…)" data-testid="stripe-pk-test" value={s.stripe_publishable_key_test || ""} onChange={(e) => set("stripe_publishable_key_test", e.target.value)} />
            <Field label="Clé secrète test (sk_test_…)" type="password" data-testid="stripe-sk-test" value={s.stripe_secret_key_test || ""} onChange={(e) => set("stripe_secret_key_test", e.target.value)} />
          </div>
        </div>
        <div className="mb-2">
          <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Clés Live (production)</div>
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Clé publique live (pk_live_…)" data-testid="stripe-pk-live" value={s.stripe_publishable_key_live || ""} onChange={(e) => set("stripe_publishable_key_live", e.target.value)} />
            <Field label="Clé secrète live (sk_live_…)" type="password" data-testid="stripe-sk-live" value={s.stripe_secret_key_live || ""} onChange={(e) => set("stripe_secret_key_live", e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-3">Le bouton <span className="font-medium">Vérifier</span> teste les clés du mode <span className="font-medium">{(s.stripe_mode || "test") === "live" ? "Live" : "Test"}</span> sélectionné. Une clé de test plateforme reste active par défaut.</p>
        <div className="flex items-center gap-2">
          <button data-testid="stripe-save" onClick={saveStripe} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
          <button data-testid="stripe-verify" onClick={async () => { await saveStripe(); verify("stripe"); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Vérifier</button>
        </div>
      </Section>
      <Section title="Serveur d'envoi d'emails (SMTP)">
        <p className="text-xs text-slate-400 mb-3">Pour la mise en production indépendante, connectez votre propre serveur SMTP (celui de votre hébergeur ou tout autre : OVH, Gmail, Brevo SMTP…). Quand il est activé, tous les emails de l'application partent par ce serveur.</p>
        <label className="flex items-center gap-2 mb-3 text-sm text-slate-700">
          <input type="checkbox" data-testid="smtp-enabled" checked={!!s.smtp_enabled} onChange={(e) => set("smtp_enabled", e.target.checked)} />
          Activer l'envoi via mon serveur SMTP
        </label>
        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label="Serveur (hôte)" data-testid="smtp-host" placeholder="smtp.exemple.com" value={s.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} />
          <div className="mb-3">
            <label className="text-sm font-medium text-slate-700">Sécurité / Port</label>
            <div className="flex gap-2 mt-1">
              <select data-testid="smtp-security" value={s.smtp_security || "starttls"} onChange={(e) => set("smtp_security", e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm">
                <option value="starttls">STARTTLS (587)</option>
                <option value="ssl">SSL/TLS (465)</option>
              </select>
              <input data-testid="smtp-port" type="number" value={s.smtp_port ?? 587} onChange={(e) => set("smtp_port", e.target.value)} className="w-24 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            </div>
          </div>
          <Field label="Identifiant (utilisateur)" data-testid="smtp-username" value={s.smtp_username || ""} onChange={(e) => set("smtp_username", e.target.value)} />
          <Field label="Mot de passe" type="password" data-testid="smtp-password" value={s.smtp_password || ""} onChange={(e) => set("smtp_password", e.target.value)} />
          <Field label="Adresse expéditeur (From)" data-testid="smtp-from" placeholder="cabinet@exemple.com" value={s.smtp_from || ""} onChange={(e) => set("smtp_from", e.target.value)} />
          <Field label="Email de test" data-testid="smtp-test-to" placeholder="vous@exemple.com" value={s.smtp_test_to || ""} onChange={(e) => set("smtp_test_to", e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="smtp-save" onClick={saveSmtp} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
          <button data-testid="smtp-test" onClick={testSmtp} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Envoyer un email de test</button>
        </div>
      </Section>
      <Section title="Emailing (Brevo / GetResponse)">
        <p className="text-xs text-slate-400 mb-3">Connectez votre outil d'emailing. Les fonctions de synchronisation seront ajoutées ensuite.</p>
        <div className="mb-3">
          <label className="text-sm font-medium text-slate-700">Fournisseur</label>
          <select data-testid="email-provider" value={s.email_provider || ""} onChange={(e) => set("email_provider", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm">
            <option value="">Aucun</option>
            <option value="brevo">Brevo</option>
            <option value="getresponse">GetResponse</option>
          </select>
        </div>
        <Field label="Clé API" type="password" data-testid="email-key" value={s.email_api_key || ""} onChange={(e) => set("email_api_key", e.target.value)} />
        {s.email_provider && (
          <div className="mt-3">
            <button type="button" data-testid="email-connect" onClick={loadEmailLists} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Se connecter &amp; charger les listes</button>
            {(emailLists.length > 0 || s.email_list_id) && (
              <div className="mt-3">
                <label className="text-sm font-medium text-slate-700">Liste par défaut</label>
                <select data-testid="email-list" value={s.email_list_id || ""} onChange={(e) => { const id = e.target.value; const name = (emailLists.find((l) => String(l.id) === String(id))?.name) || (id === (s.email_list_id || "") ? (s.email_list_name || "") : ""); setS({ ...s, email_list_id: id, email_list_name: name }); }} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm">
                  <option value="">Choisir une liste…</option>
                  {emailLists.length === 0 && s.email_list_id && <option value={s.email_list_id}>{s.email_list_name || s.email_list_id}</option>}
                  {emailLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                {s.email_list_id && <p className="text-xs text-slate-500 mt-1" data-testid="email-list-current">Liste sélectionnée : <span className="font-medium text-slate-700">{s.email_list_name || s.email_list_id}</span></p>}
              </div>
            )}
          </div>
        )}
        <div className="flex items-center gap-2 mt-4">
          <button data-testid="email-save" onClick={saveEmail} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
          <button data-testid="email-verify" onClick={async () => { await saveEmail(); await loadEmailLists(); verify("email"); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Vérifier</button>
        </div>
      </Section>
      <Section title="Twilio (SMS / WhatsApp)">
        <p className="text-xs text-slate-400 mb-3">Utilisé pour l'envoi des liens de paiement et des messages de tournée par SMS.</p>
        <div className="grid sm:grid-cols-3 gap-x-4">
          <Field label="Account SID" data-testid="twilio-sid" value={s.twilio_account_sid || ""} onChange={(e) => set("twilio_account_sid", e.target.value)} />
          <Field label="Auth Token" type="password" data-testid="twilio-token" value={s.twilio_auth_token || ""} onChange={(e) => set("twilio_auth_token", e.target.value)} />
          <Field label="Expéditeur (+33… ou MG…)" data-testid="twilio-from" value={s.twilio_from || ""} onChange={(e) => set("twilio_from", e.target.value)} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button data-testid="twilio-save" onClick={saveTwilio} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
          <input data-testid="twilio-test-to" value={twilioTestTo} onChange={(e) => setTwilioTestTo(e.target.value)} placeholder="N° de test (+33…)" className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <button data-testid="twilio-test" onClick={testTwilio} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Tester</button>
        </div>
      </Section>
    </div>
  );
}

function FacturePanel() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const save = async () => { await api.put("/settings", s); toast.success("Enregistré"); };
  const set = (k, v) => setS({ ...s, [k]: v });
  return (
    <Section title="Modèle de facture">
      <label className="flex items-center gap-2 text-sm text-slate-700 mb-3"><input type="checkbox" data-testid="facture-exo" checked={s.exonere_tva_defaut ?? true} onChange={(e) => set("exonere_tva_defaut", e.target.checked)} />Exonéré de TVA par défaut (ostéopathie)</label>
      <Field label="Taux de TVA par défaut (%)" type="number" value={s.taux_tva_defaut ?? 0} onChange={(e) => set("taux_tva_defaut", Number(e.target.value))} />
      <div className="mb-3"><label className="text-sm font-medium text-slate-700">Mentions de bas de facture</label><textarea rows={3} value={s.facture_pied || ""} onChange={(e) => set("facture_pied", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" /></div>
      <div className="border-t border-slate-100 pt-3 mb-3">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Signature (bas de facture PDF)</div>
        <div className="mb-3"><label className="text-sm font-medium text-slate-700">Texte au-dessus de la signature</label><textarea data-testid="ps-sig-text" rows={2} value={s.facture_signature_texte || ""} onChange={(e) => set("facture_signature_texte", e.target.value)} placeholder="Ex. : Fait à Marseille, le…" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" /></div>
        <div className="flex items-center gap-4">
          <div className="w-40 h-20 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
            {s.facture_signature_url ? <img src={s.facture_signature_url} alt="Signature" data-testid="ps-sig-preview" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-slate-400 px-2 text-center">Aucune signature</span>}
          </div>
          <div>
            <input type="file" accept="image/*" className="hidden" id="ps-sig-input" onChange={(e) => { const f = e.target.files[0]; if (!f) return; const rd = new FileReader(); rd.onload = () => set("facture_signature_url", rd.result); rd.readAsDataURL(f); }} />
            <button type="button" data-testid="ps-sig-btn" onClick={() => document.getElementById("ps-sig-input").click()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"><Upload className="w-4 h-4" />Choisir une signature</button>
            {s.facture_signature_url && <button type="button" data-testid="ps-sig-clear" onClick={() => set("facture_signature_url", "")} className="ml-2 text-sm text-red-500">Retirer</button>}
            <p className="text-xs text-slate-400 mt-2">PNG transparent conseillé. Apparaît en bas à droite de la facture après « Enregistrer ».</p>
          </div>
        </div>
      </div>
      <button onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
    </Section>
  );
}

const IMPORT_CARDS = [
  { kind: "clients", icon: Users, titre: "Importer les clients", desc: "Depuis un fichier CSV (nom, contact, secteur, tags…)." },
  { kind: "soins", icon: FileText, titre: "Importer des fiches de soins", desc: "Un ou plusieurs fichiers .md avec en-tête patient/date/titre." },
  { kind: "rdvs", icon: CalendarClock, titre: "Importer les RDV", desc: "Depuis un CSV ; chaque RDV crée aussi sa fiche de soin." },
];

function ShortcutsPanel() {
  const [map, setMap] = useState(loadShortcuts());
  const [capturing, setCapturing] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => { if (r.data?.raccourcis) setMap((m) => { const n = { ...m }; Object.keys(DEFAULTS).forEach((id) => { if (r.data.raccourcis[id]) n[id] = r.data.raccourcis[id]; }); return n; }); }); }, []);

  // Capture globale de la combinaison pendant l'édition (empêche l'action navigateur quand c'est possible)
  useEffect(() => {
    if (!capturing) return;
    const onKey = (e) => {
      if (["Shift", "Control", "Alt", "Meta", "OS", "CapsLock"].includes(e.key)) return; // attendre la vraie touche
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Escape") { setCapturing(null); return; }
      const key = (e.key || "").toLowerCase();
      if (!(key.length === 1 && /[a-z0-9]/.test(key))) return;
      const sc = { mod: e.metaKey || e.ctrlKey, alt: e.altKey, shift: e.shiftKey, key };
      setMap((m) => ({ ...m, [capturing]: sc }));
      setCapturing(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [capturing]);

  const dupOf = (id) => SHORTCUT_DEFS.some((d) => d.id !== id && sameCombo(map[d.id], map[id]));
  const save = async () => {
    if (SHORTCUT_DEFS.some((d) => dupOf(d.id))) { toast.error("Deux raccourcis utilisent la même combinaison"); return; }
    await api.put("/settings", { raccourcis: map });
    saveShortcutsLocal(map);
    toast.success("Raccourcis enregistrés");
  };
  const reset = (id) => setMap((m) => ({ ...m, [id]: { ...DEFAULTS[id] } }));
  return (
    <div className="space-y-4">
      <Section title="Raccourcis clavier personnalisables">
        <p className="text-xs text-slate-400 mb-4">Cliquez sur un raccourci puis appuyez sur la <b>combinaison complète</b> souhaitée (ex. ⌘/Ctrl + N, Alt + K, Maj + P…). L'application l'enregistre et tente de bloquer l'action par défaut du navigateur. ⚠️ Quelques combinaisons système (⌘/Ctrl + N, T, W, Q…) restent réservées par le navigateur et ne peuvent pas toujours être interceptées — préférez alors une autre combinaison. Les raccourcis sont ignorés pendant la saisie dans un champ.</p>
        {SHORTCUT_GROUPS.map((g) => (
          <div key={g.group} className="mb-5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">{g.group}</div>
            <div className="space-y-1.5">
              {g.items.map((it) => {
                const sc = map[it.id];
                const conflict = dupOf(it.id);
                const reserved = isReserved(sc);
                return (
                  <div key={it.id} className="flex items-center justify-between gap-3 py-1" data-testid={`shortcut-row-${it.id}`}>
                    <div className="text-sm text-slate-700">
                      {it.label}
                      {reserved && <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-amber-600"><AlertTriangle className="w-3 h-3" />réservé par le navigateur</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`shortcut-key-${it.id}`}
                        onClick={() => setCapturing(it.id)}
                        className="min-w-[104px] px-3 py-1.5 rounded-lg border text-sm font-mono text-center transition-colors"
                        style={capturing === it.id
                          ? { borderColor: "var(--ink)", background: "var(--accent)", color: "var(--ink)" }
                          : { borderColor: conflict ? "#ef4444" : "#cbd5e1", background: "#fff", color: "var(--ink)" }}>
                        {capturing === it.id ? "Appuyez…" : comboLabel(sc)}
                      </button>
                      <button onClick={() => reset(it.id)} title="Réinitialiser" data-testid={`shortcut-reset-${it.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><RotateCcw className="w-4 h-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <button data-testid="shortcuts-save" onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
      </Section>
      <Section title="Raccourcis de navigation (fixes)">
        <div className="space-y-2">
          {FIXED_SHORTCUTS.map((f, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{f.label}</span>
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-500 font-mono text-[12px]">{f.keys}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function DataPanel() {
  const [wizard, setWizard] = useState(null);
  const patFileRef = useRef(null);
  const setFileRef = useRef(null);
  const importPatientsFull = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/import-patients-full", fd); const n = Object.values(data.collections || {}).reduce((a, b) => a + b, 0); toast.success(`${n} enregistrement(s) importé(s)`); }
    catch (e) { toast.error("Import impossible : " + (e.response?.data?.detail || e.message)); }
  };
  const importSettings = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/import-settings", fd); toast.success(`Réglages importés (${data.champs} champs)`); }
    catch (e) { toast.error("Import impossible : " + (e.response?.data?.detail || e.message)); }
  };
  return (
    <div className="space-y-4">
      <Section title="Importer des données">
        <p className="text-xs text-slate-400 mb-3">Analyse assistée par IA : déposez vos fichiers, vérifiez et corrigez les données proposées, puis validez l'import.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {IMPORT_CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <button key={c.kind} data-testid={`import-open-${c.kind}`} onClick={() => setWizard(c.kind)}
                className="text-left sx-card p-4 hover:shadow-md transition-shadow bg-white">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "var(--accent)" }}><Icon className="w-5 h-5 text-[#141821]" /></div>
                <div className="text-[14px] font-semibold" style={{ color: "var(--ink)" }}>{c.titre}</div>
                <div className="text-[12px] text-slate-500 mt-0.5">{c.desc}</div>
              </button>
            );
          })}
        </div>
      </Section>
      <Section title="Importer un jeu de données complet (JSON)">
        <p className="text-xs text-slate-400 mb-3">Restaurez un export complet (patients, RDV, soins, factures, paiements) ou vos réglages — utile lors d'une migration. Les enregistrements existants portant le même identifiant sont mis à jour.</p>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="import-patients-full" onClick={() => patFileRef.current?.click()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm"><UploadCloud className="w-4 h-4" />Importer toutes mes données patients</button>
          <input ref={patFileRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => { importPatientsFull(e.target.files?.[0]); e.target.value = ""; }} />
          <button data-testid="import-settings-data" onClick={() => setFileRef.current?.click()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm"><Settings2 className="w-4 h-4" />Importer les réglages</button>
          <input ref={setFileRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => { importSettings(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </Section>
      {wizard && <ImportWizard kind={wizard} onClose={() => setWizard(null)} />}
    </div>
  );
}

function ExportPanel() {
  const [pending, setPending] = useState(null); // { title, message, run } — confirmation "JE CONFIRME"
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const settingsFileRef = useRef(null);
  const dbFileRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const tok = () => localStorage.getItem("sx_token");

  const download = async (url, filename) => {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${tok()}` } });
    if (!r.ok) { toast.error("Échec du téléchargement"); return; }
    const b = await r.blob();
    const u = URL.createObjectURL(b); const a = document.createElement("a");
    a.href = u; a.download = filename; a.click(); URL.revokeObjectURL(u);
  };

  const dlCsv = (kind) => download(`${BACKEND}/api/dashboard/export/${kind}`, `${kind}.csv`);
  const exportSettings = () => download(`${BACKEND}/api/admin/export-settings`, "soinxpower-reglages.json");

  const importSettings = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/import-settings", fd); toast.success(`Réglages importés (${data.champs} champs)`); }
    catch (e) { toast.error("Import impossible : " + (e.response?.data?.detail || e.message)); }
  };

  const askExportDb = () => setPending({
    title: "Télécharger toute la base de données",
    message: "Vous allez télécharger l'intégralité des données de l'application (patients, RDV, soins, factures, réglages…). Ce fichier est sensible : conservez-le en lieu sûr.",
    run: async () => { await download(`${BACKEND}/api/admin/export-db?confirm=${encodeURIComponent("JE CONFIRME")}`, "soinxpower-db.json"); toast.success("Sauvegarde téléchargée"); },
  });

  const askImportDb = (file) => setPending({
    title: "Importer toute la base de données",
    message: `⚠️ Opération destructive et irréversible. Le contenu actuel sera REMPLACÉ par « ${file.name} ». Toutes les données existantes seront écrasées.`,
    danger: true,
    run: async () => {
      const fd = new FormData(); fd.append("file", file); fd.append("confirm", "JE CONFIRME");
      const { data } = await api.post("/admin/import-db", fd);
      const n = Object.values(data.collections || {}).reduce((a, b) => a + b, 0);
      toast.success(`Base restaurée (${n} enregistrements). Rechargez l'application.`);
    },
  });

  const runConfirm = async () => {
    if (txt !== "JE CONFIRME" || !pending) return;
    setBusy(true);
    try { await pending.run(); }
    catch (e) { toast.error("Échec : " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); setPending(null); setTxt(""); }
  };

  return (
    <div className="space-y-4">
      <Section title="Exporter mes données (CSV)">
        <div className="flex gap-2 flex-wrap">
          <button data-testid="data-export-patients" onClick={() => dlCsv("patients")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm"><Download className="w-4 h-4" />Patients</button>
          <button onClick={() => dlCsv("factures")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm"><Download className="w-4 h-4" />Factures</button>
          <button onClick={() => dlCsv("soins")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm"><Download className="w-4 h-4" />Soins</button>
          <button data-testid="export-patients-full" onClick={() => download(`${BACKEND}/api/admin/export-patients-full`, "soinxpower-patients.json")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium"><Database className="w-4 h-4" />Exporter toutes mes données patients</button>
        </div>
      </Section>

      <Section title="Réglages de l'application">
        <p className="text-xs text-slate-400 mb-3">Sauvegardez ou restaurez tous les paramètres (praticien, facturation, connexions, raccourcis…) sous forme d'un fichier JSON.</p>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="export-settings" onClick={exportSettings} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm"><Download className="w-4 h-4" />Exporter les réglages</button>
          <button data-testid="import-settings" onClick={() => settingsFileRef.current?.click()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-sm"><Settings2 className="w-4 h-4" />Importer les réglages</button>
          <input ref={settingsFileRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => { importSettings(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </Section>

      <Section title="Base de données complète — migration">
        <div className="sx-card p-4 border border-amber-200 bg-amber-50/40">
          <div className="flex items-start gap-2 mb-3">
            <Database className="w-5 h-5 text-amber-600 mt-0.5" />
            <p className="text-[12.5px] text-slate-600">Pour migrer l'application vers un autre serveur : téléchargez toute la base ici, puis importez-la sur la nouvelle installation. Ces deux opérations demandent une confirmation en toutes lettres.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="export-db" onClick={askExportDb} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium"><Download className="w-4 h-4" />Télécharger toute la DB</button>
            <button data-testid="import-db" onClick={() => dbFileRef.current?.click()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium"><UploadCloud className="w-4 h-4" />Importer toute la DB</button>
            <input ref={dbFileRef} type="file" accept=".json,application/json" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) askImportDb(f); }} />
          </div>
        </div>
      </Section>

      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) { setPending(null); setTxt(""); } }}>
        <DialogContent data-testid="confirm-type-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{pending?.danger && <AlertTriangle className="w-5 h-5 text-red-600" />}{pending?.title}</DialogTitle>
            <DialogDescription className="pt-1">{pending?.message}</DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <label className="text-sm text-slate-600">Pour confirmer, écrivez <b>JE CONFIRME</b> ci-dessous :</label>
            <input data-testid="confirm-type-input" autoFocus value={txt} onChange={(e) => setTxt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runConfirm(); }}
              placeholder="JE CONFIRME"
              className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono" />
          </div>
          <DialogFooter>
            <button onClick={() => { setPending(null); setTxt(""); }} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
            <button data-testid="confirm-type-run" onClick={runConfirm} disabled={txt !== "JE CONFIRME" || busy}
              className={`px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-40 ${pending?.danger ? "bg-red-600" : "bg-[#141821]"}`}>
              {busy ? "Traitement…" : "Confirmer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SecteurPanel() {
  const [items, setItems] = useState([]);
  const [nom, setNom] = useState("");
  const [del, setDel] = useState(null);
  const [rep, setRep] = useState("");
  const [editId, setEditId] = useState(null);
  const [editNom, setEditNom] = useState("");
  const load = () => api.get("/settings/secteurs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!nom.trim()) return; await api.post("/settings/secteurs", { nom }); setNom(""); load(); toast.success("Secteur ajouté"); };
  const saveEdit = async (s) => {
    if (!editNom.trim()) return;
    await api.put(`/settings/secteurs/${s.id}`, { nom: editNom, codes_postaux: s.codes_postaux || [], villes: s.villes || [] });
    setEditId(null); load(); toast.success("Secteur modifié");
  };
  const ask = async (s) => {
    const { data } = await api.get(`/settings/secteurs/${s.id}/usage`);
    setRep(""); setDel({ ...s, count: data.count });
  };
  const remove = async (id, replacement) => {
    await api.delete(`/settings/secteurs/${id}`, { params: replacement ? { replacement } : {} });
    setDel(null); load(); toast.success("Secteur supprimé");
  };
  return (
    <Section title="Secteurs d'intervention">
      <div className="space-y-2 mb-4">
        {items.length === 0 && <div className="text-sm text-slate-400">Aucun secteur.</div>}
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between py-2 px-3 border border-slate-100 rounded-lg text-sm">
            {editId === s.id ? (
              <>
                <input data-testid={`secteur-edit-${s.id}`} value={editNom} onChange={(e) => setEditNom(e.target.value)} className="flex-1 mr-2 px-2 py-1.5 rounded border border-slate-300 text-sm" autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") saveEdit(s); if (e.key === "Escape") setEditId(null); }} />
                <div className="flex items-center gap-2">
                  <button data-testid={`secteur-edit-save-${s.id}`} onClick={() => saveEdit(s)} className="text-emerald-600"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditId(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
                </div>
              </>
            ) : (
              <>
                <span>{s.nom}</span>
                <div className="flex items-center gap-2">
                  <button data-testid={`secteur-edit-${s.id}`} onClick={() => { setEditId(s.id); setEditNom(s.nom); }} className="text-slate-400 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`secteur-del-${s.id}`} onClick={() => ask(s)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 border-t border-slate-100 pt-4">
        <input data-testid="secteur-input-nom" placeholder="Nom du secteur" value={nom} onChange={(e) => setNom(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        <button data-testid="secteur-add" onClick={add} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Plus className="w-4 h-4" />Ajouter</button>
      </div>

      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Supprimer le secteur</DialogTitle><DialogDescription>Cette action retire le secteur ; les patients qui y sont rattachés ne seront pas supprimés.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm">
            {del?.count > 0 ? (
              <>
                <p><span className="font-semibold">{del?.count}</span> patient(s) sont associés au secteur « {del?.nom} ». Souhaitez-vous réaffecter ces patients à un autre secteur ?</p>
                <select data-testid="secteur-replacement" value={rep} onChange={(e) => setRep(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300">
                  <option value="">Aucun — retirer simplement l'association</option>
                  {items.filter((s) => s.id !== del?.id).map((s) => <option key={s.id} value={s.id}>Remplacer par : {s.nom}</option>)}
                </select>
              </>
            ) : (
              <p>Supprimer le secteur « {del?.nom} » ? Cette action est irréversible.</p>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setDel(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
            <button data-testid="secteur-confirm-delete" onClick={() => remove(del.id, rep)} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium">Supprimer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}

function TramePanel() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [edit, setEdit] = useState(null); // {id?, nom, contenu_md, par_defaut, prestations}
  const load = () => api.get("/settings/soin-templates").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/prestations").then((r) => setPrestations(r.data)).catch(() => {}); }, []);
  const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
  const lineTtc = (l) => (Number(l.qte) || 0) * (Number(l.prix_unitaire) || 0) * (1 + (Number(l.tva) || 0) / 100);
  const addPresta = (p) => setEdit((c) => ({ ...c, prestations: [...(c.prestations || []), p ? { libelle: p.libelle, qte: 1, prix_unitaire: p.prix_unitaire, tva: p.tva, prestation_id: p.id } : { libelle: "", qte: 1, prix_unitaire: 0, tva: 0, prestation_id: null }] }));
  const updPresta = (i, k, v) => setEdit((c) => ({ ...c, prestations: (c.prestations || []).map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const delPresta = (i) => setEdit((c) => ({ ...c, prestations: (c.prestations || []).filter((_, idx) => idx !== i) }));
  const save = async () => {
    if (!edit.nom?.trim()) { toast.error("Nom requis"); return; }
    try {
      const body = { nom: edit.nom, contenu_md: edit.contenu_md || "", par_defaut: !!edit.par_defaut, prestations: edit.prestations || [] };
      if (edit.id) await api.put(`/settings/soin-templates/${edit.id}`, body);
      else await api.post("/settings/soin-templates", body);
      setEdit(null); load(); toast.success("Trame enregistrée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!(await confirm({ message: "Supprimer cette trame ?" }))) return;
    await api.delete(`/settings/soin-templates/${id}`); if (edit?.id === id) setEdit(null); load(); toast.success("Trame supprimée");
  };
  if (edit) {
    const pr = edit.prestations || [];
    return (
      <Section title={edit.id ? "Modifier la trame" : "Nouvelle trame"}>
        <Field label="Nom de la trame" data-testid="trame-edit-nom" value={edit.nom || ""} onChange={(e) => setEdit({ ...edit, nom: e.target.value })} />
        <label className="flex items-center gap-2 text-sm text-slate-700 mb-3">
          <input type="checkbox" data-testid="trame-edit-default" checked={!!edit.par_defaut} onChange={(e) => setEdit({ ...edit, par_defaut: e.target.checked })} />
          Trame par défaut (ajoutée automatiquement au texte des nouveaux soins)
        </label>
        <div className="mb-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <RichMarkdownEditor markdown={edit.contenu_md || ""} onChange={(v) => setEdit((cur) => ({ ...cur, contenu_md: v }))} />
        </div>
        <div className="mb-4 border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 text-sm font-semibold text-slate-700">Prestations à insérer dans la facture</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-3 py-2 font-semibold">Libellé</th><th className="px-3 py-2 font-semibold w-16">Qté</th><th className="px-3 py-2 font-semibold w-28">PU (€)</th><th className="px-3 py-2 font-semibold w-20">TVA %</th><th className="px-3 py-2 font-semibold text-right w-28">Total TTC</th><th className="px-3 py-2 w-8"></th>
              </tr></thead>
              <tbody>
                {pr.length === 0 ? <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Aucune prestation. Ajoutez-en ci-dessous.</td></tr>
                  : pr.map((l, i) => (
                    <tr key={i} data-testid={`trame-presta-${i}`} className="border-b border-slate-50 last:border-0">
                      <td className="px-3 py-2"><input data-testid={`trame-presta-libelle-${i}`} value={l.libelle} onChange={(e) => updPresta(i, "libelle", e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm" /></td>
                      <td className="px-3 py-2"><input type="number" step="0.5" value={l.qte} onChange={(e) => updPresta(i, "qte", Number(e.target.value))} className="w-14 px-2 py-1.5 rounded-lg border border-slate-200 text-sm" /></td>
                      <td className="px-3 py-2"><input type="number" step="0.01" value={l.prix_unitaire} onChange={(e) => updPresta(i, "prix_unitaire", Number(e.target.value))} className="w-24 px-2 py-1.5 rounded-lg border border-slate-200 text-sm" /></td>
                      <td className="px-3 py-2"><input type="number" step="0.1" value={l.tva} onChange={(e) => updPresta(i, "tva", Number(e.target.value))} className="w-16 px-2 py-1.5 rounded-lg border border-slate-200 text-sm" /></td>
                      <td className="px-3 py-2 text-right font-medium text-slate-900">{eur(lineTtc(l))}</td>
                      <td className="px-3 py-2"><button data-testid={`trame-presta-del-${i}`} onClick={() => delPresta(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
            <button data-testid="trame-presta-add" onClick={() => addPresta(null)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"><Plus className="w-4 h-4" />Ligne libre</button>
            {prestations.length > 0 && (
              <select data-testid="trame-presta-catalogue" defaultValue="" onChange={(e) => { const p = prestations.find((x) => x.id === e.target.value); if (p) addPresta(p); e.target.value = ""; }} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="">+ Depuis le catalogue…</option>
                {prestations.map((p) => <option key={p.id} value={p.id}>{`${p.libelle} — ${eur(p.prix_unitaire)}`}</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="trame-edit-save" onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Save className="w-4 h-4" />Enregistrer</button>
          <button data-testid="trame-edit-cancel" onClick={() => setEdit(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
        </div>
      </Section>
    );
  }
  return (
    <Section title="Trames de fiche de soin">
      <div className="space-y-2 mb-4">
        {items.length === 0 && <div className="text-sm text-slate-400">Aucune trame.</div>}
        {items.map((x) => (
          <div key={x.id} data-testid={`trame-row-${x.id}`} className="flex items-center justify-between py-2 px-3 border border-slate-100 rounded-lg text-sm">
            <button data-testid={`trame-edit-${x.id}`} onClick={() => setEdit({ id: x.id, nom: x.nom, contenu_md: x.contenu_md || "", par_defaut: !!x.par_defaut, prestations: x.prestations || [] })} className="flex items-center gap-2 text-left hover:text-emerald-700">
              {x.nom}
              {(x.prestations || []).length > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{x.prestations.length} presta.</span>}
              {x.par_defaut && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800"><Star className="w-3 h-3" />Par défaut</span>}
            </button>
            <button data-testid={`trame-del-${x.id}`} onClick={() => del(x.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button data-testid="trame-new" onClick={() => setEdit({ nom: "", contenu_md: "", par_defaut: false, prestations: [] })} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Plus className="w-4 h-4" />Nouvelle trame</button>
    </Section>
  );
}

function TagsPanel() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null); // {id?, titre, couleur}
  const [del, setDel] = useState(null); // {tag, count, replacement}
  const load = () => api.get("/settings/tags").then((r) => setItems([...r.data].sort((a, b) => (a.titre || "").localeCompare(b.titre || ""))));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!edit.titre?.trim()) { toast.error("Nom requis"); return; }
    try {
      const body = { titre: edit.titre, couleur: edit.couleur, cible: "patient" };
      if (edit.id) await api.put(`/settings/tags/${edit.id}`, body);
      else await api.post("/settings/tags", body);
      setEdit(null); load(); toast.success("Tag enregistré");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const askDelete = async (t) => {
    const { data } = await api.get(`/settings/tags/${t.id}/usage`);
    if (data.count > 0) setDel({ tag: t, count: data.count, replacement: "" });
    else { await api.delete(`/settings/tags/${t.id}`); load(); toast.success("Tag supprimé"); }
  };
  const confirmDelete = async () => {
    const q = del.replacement ? `?replacement=${del.replacement}` : "";
    await api.delete(`/settings/tags/${del.tag.id}${q}`); setDel(null); load(); toast.success("Tag supprimé");
  };
  return (
    <Section title="Tags patients">
      <div className="space-y-2 mb-4">
        {items.length === 0 && <div className="text-sm text-slate-400">Aucun tag.</div>}
        {items.map((t) => { const parts = (t.titre || "").split("/"); const depth = parts.length - 1; const leaf = parts[parts.length - 1]; const parent = parts.slice(0, -1).join(" / "); return (
          <div key={t.id} data-testid={`tag-row-${t.id}`} className="flex items-center justify-between py-2 px-3 border border-slate-100 rounded-lg text-sm" style={{ marginLeft: depth * 16 }}>
            <button data-testid={`tag-edit-${t.id}`} onClick={() => setEdit({ id: t.id, titre: t.titre, couleur: t.couleur })} className="flex items-center gap-2 text-left hover:opacity-80">
              <span className="w-4 h-4 rounded shrink-0" style={{ background: t.couleur }} />
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium" style={tagChipStyle(t.couleur)}>
                {depth > 0 ? <><span style={{ opacity: 0.55 }}>{parent} / </span>{leaf}</> : t.titre}
              </span>
            </button>
            <button data-testid={`tag-del-${t.id}`} onClick={() => askDelete(t)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ); })}
      </div>
      {edit ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
          <input title="Couleur" data-testid="tag-edit-couleur" type="color" value={edit.couleur || SWATCH_DEFAULT} onChange={(e) => setEdit({ ...edit, couleur: e.target.value })} className="w-11 h-11 p-1 rounded-lg border border-slate-300 cursor-pointer shrink-0" />
          <input data-testid="tag-edit-titre" placeholder="Nom du tag" value={edit.titre || ""} onChange={(e) => setEdit({ ...edit, titre: e.target.value })} className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <button data-testid="tag-edit-save" onClick={save} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium shrink-0"><Save className="w-4 h-4" />{edit.id ? "Enregistrer" : "Ajouter"}</button>
          <button onClick={() => setEdit(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm shrink-0">Annuler</button>
        </div>
      ) : (
        <button data-testid="tag-new" onClick={() => setEdit({ titre: "", couleur: randomPastel() })} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Plus className="w-4 h-4" />Nouveau tag</button>
      )}
      {del && (
        <Dialog open onOpenChange={(o) => !o && setDel(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Supprimer « {del.tag.titre} »</DialogTitle>
              <DialogDescription>{del.count} patient(s) portent ce tag. Choisissez un tag de remplacement, ou laissez vide pour simplement le retirer.</DialogDescription>
            </DialogHeader>
            <select data-testid="tag-del-replacement" value={del.replacement} onChange={(e) => setDel({ ...del, replacement: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
              <option value="">— Retirer sans remplacement —</option>
              {items.filter((t) => t.id !== del.tag.id).map((t) => <option key={t.id} value={t.id}>{t.titre}</option>)}
            </select>
            <DialogFooter>
              <button onClick={() => setDel(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
              <button data-testid="tag-del-confirm" onClick={confirmDelete} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium">Supprimer</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Section>
  );
}

// Grille de tons (prompt 14) : 4×2 pastilles, ton courant entouré, point gris sur les tons déjà pris.
function ToneSwatches({ current, used, onPick, size = 28 }) {
  return (
    <div className="grid grid-cols-4 gap-2" style={{ width: size * 4 + 24 }}>
      {CARE_TYPE_TONES.map((t) => (
        <button key={t.key} data-testid={`tone-${t.key}`} title={t.label} onClick={() => onPick(t.key)}
          className="relative" style={{ width: size, height: size, borderRadius: 8, background: t.bg, border: `1px solid ${t.border}`, boxShadow: current === t.key ? "0 0 0 2px #2f5d4a" : "none" }}>
          {used?.has(t.key) && current !== t.key && <span style={{ position: "absolute", right: 3, bottom: 3, width: 4, height: 4, borderRadius: 99, background: "#9aa0ab" }} />}
        </button>
      ))}
    </div>
  );
}

// Popover de sélection de ton (backdrop pour fermer au clic en dehors).
function TonePopover({ current, used, onPick, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
      <div data-testid="tone-popover" className="absolute left-0 top-6 bg-white p-2.5 rounded-lg" style={{ border: "1px solid var(--border)", boxShadow: "var(--sh-float)", zIndex: 41 }}>
        <ToneSwatches current={current} used={used} onPick={onPick} />
      </div>
    </>
  );
}

// Types de soin : liste avec pastille de couleur + menu de tons, création avec ton présélectionné.
function CareTypePanel() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [nom, setNom] = useState(""); const [duree, setDuree] = useState(60);
  const [newKey, setNewKey] = useState(CARE_TYPE_TONES[0].key);
  const [edit, setEdit] = useState(null);     // { id, nom, duree }
  const [picker, setPicker] = useState(null); // id du type dont le menu est ouvert
  const load = () => api.get("/settings/care-types").then((r) => { setItems(r.data); setNewKey(firstFreeToneKey(r.data)); });
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!nom.trim()) { toast.error("Nom requis"); return; }
    try { await api.post("/settings/care-types", { nom: nom.trim(), duree: Number(duree) || 60, color_key: newKey }); setNom(""); setDuree(60); load(); toast.success("Ajouté"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!(await confirm({ message: "Supprimer ce type de soin ?" }))) return; await api.delete(`/settings/care-types/${id}`); load(); toast.success("Supprimé"); };
  const saveEdit = async () => {
    try { await api.put(`/settings/care-types/${edit.id}`, { nom: edit.nom, duree: Number(edit.duree) || 60 }); setEdit(null); load(); toast.success("Modifié"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setColor = async (x, key) => {
    setItems((prev) => prev.map((i) => (i.id === x.id ? { ...i, color_key: key } : i))); setPicker(null);
    try { await api.put(`/settings/care-types/${x.id}`, { nom: x.nom, duree: x.duree || 60, color_key: key }); toast.success("Couleur enregistrée"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };
  return (
    <Section title="Types de soin">
      <div className="space-y-2 mb-4">
        {items.length === 0 && <div className="text-sm text-slate-400">Aucun type de soin.</div>}
        {items.map((x) => { const tone = toneForType(x); return edit?.id === x.id ? (
          <div key={x.id} className="flex flex-wrap items-center gap-2 py-2 px-3 border border-slate-200 rounded-lg">
            <input data-testid={`type-edit-nom-${x.id}`} value={edit.nom} onChange={(e) => setEdit({ ...edit, nom: e.target.value })} className="flex-1 min-w-[120px] px-2 py-1.5 rounded border border-slate-300 text-sm" />
            <input data-testid={`type-edit-duree-${x.id}`} type="number" value={edit.duree} onChange={(e) => setEdit({ ...edit, duree: e.target.value })} className="w-24 px-2 py-1.5 rounded border border-slate-300 text-sm" />
            <button data-testid={`type-edit-save-${x.id}`} onClick={saveEdit} className="text-emerald-600"><Check className="w-4 h-4" /></button>
            <button onClick={() => setEdit(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div key={x.id} className="flex items-center justify-between py-2 px-3 border border-slate-100 rounded-lg text-sm">
            <div className="flex items-center gap-2.5 relative">
              <button data-testid={`type-color-${x.id}`} onClick={() => setPicker(picker === x.id ? null : x.id)} title="Changer la couleur" style={{ width: 16, height: 16, borderRadius: 99, background: tone.bg, border: `1px solid ${tone.border}`, flexShrink: 0 }} />
              {picker === x.id && <TonePopover current={x.color_key} used={usedToneKeys(items, x.id)} onPick={(k) => setColor(x, k)} onClose={() => setPicker(null)} />}
              <span style={{ color: "var(--ink)" }}>{x.nom} · {x.duree || 60} min</span>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`type-edit-${x.id}`} onClick={() => setEdit({ id: x.id, nom: x.nom, duree: x.duree || 60 })} className="text-slate-400 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`type-del-${x.id}`} onClick={() => del(x.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ); })}
      </div>
      <div className="border-t border-slate-100 pt-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="type-input-nom" placeholder="Nom" value={nom} onChange={(e) => setNom(e.target.value)} className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <input data-testid="type-input-duree" type="number" placeholder="Durée (min)" value={duree} onChange={(e) => setDuree(e.target.value)} className="w-32 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <button data-testid="type-add" onClick={add} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium shrink-0"><Plus className="w-4 h-4" />Ajouter</button>
        </div>
        <div>
          <div className="text-[12px] mb-1.5" style={{ color: "var(--muted-2)" }}>Couleur</div>
          <ToneSwatches current={newKey} used={usedToneKeys(items)} onPick={setNewKey} />
        </div>
      </div>
    </Section>
  );
}

function ListPanel({ title, endpoint, fields, render, testid, extra = {}, initF, editable = false }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [f, setF] = useState(() => (initF ? initF() : {}));
  const [edit, setEdit] = useState(null);
  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);
  const add = async () => {
    try { await api.post(endpoint, { ...extra, ...f }); setF(initF ? initF() : {}); load(); toast.success("Ajouté"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!(await confirm({ message: "Supprimer cet élément ?" }))) return; await api.delete(`${endpoint}/${id}`); load(); toast.success("Supprimé"); };
  const startEdit = (x) => { const v = {}; fields.forEach(([k]) => { v[k] = x[k] ?? ""; }); setEdit({ id: x.id, values: v }); };
  const saveEdit = async () => {
    try { await api.put(`${endpoint}/${edit.id}`, { ...extra, ...edit.values }); setEdit(null); load(); toast.success("Modifié"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Section title={title}>
      <div className="space-y-2 mb-4">
        {items.length === 0 && <div className="text-sm text-slate-400">Aucun élément.</div>}
        {items.map((x) => edit?.id === x.id ? (
          <div key={x.id} className="flex flex-wrap items-center gap-2 py-2 px-3 border border-slate-200 rounded-lg">
            {fields.map(([k, l, type]) => (
              <input key={k} data-testid={`${testid}-edit-${k}-${x.id}`} type={type === "number" ? "number" : "text"} placeholder={l}
                value={edit.values[k] ?? ""} onChange={(e) => setEdit({ ...edit, values: { ...edit.values, [k]: type === "number" ? Number(e.target.value) : e.target.value } })}
                className="flex-1 min-w-[120px] px-2 py-1.5 rounded border border-slate-300 text-sm" />
            ))}
            <button data-testid={`${testid}-edit-save-${x.id}`} onClick={saveEdit} className="text-emerald-600"><Check className="w-4 h-4" /></button>
            <button onClick={() => setEdit(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div key={x.id} className="flex items-center justify-between py-2 px-3 border border-slate-100 rounded-lg text-sm">
            <span>{render(x)}</span>
            <div className="flex items-center gap-2">
              {editable && <button data-testid={`${testid}-edit-${x.id}`} onClick={() => startEdit(x)} className="text-slate-400 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>}
              <button data-testid={`${testid}-del-${x.id}`} onClick={() => del(x.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        {fields.map(([k, l, type]) => type === "textarea"
          ? <textarea key={k} placeholder={l} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} rows={4} className="w-full basis-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono-code" />
          : type === "color"
            ? <input key={k} title={l} data-testid={`${testid}-input-${k}`} type="color" value={f[k] || SWATCH_DEFAULT} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="w-11 h-11 p-1 rounded-lg border border-slate-300 cursor-pointer shrink-0" />
            : <input key={k} data-testid={`${testid}-input-${k}`} type={type === "number" ? "number" : "text"} placeholder={l} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: type === "number" ? Number(e.target.value) : e.target.value })} className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-slate-300 text-sm" />)}
        <button data-testid={`${testid}-add`} onClick={add} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium shrink-0"><Plus className="w-4 h-4" />Ajouter</button>
      </div>
    </Section>
  );
}


const eur = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

function CataloguePanel() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("toutes");
  const [edit, setEdit] = useState(null);
  const load = () => { api.get("/prestations").then((r) => setItems(r.data)); api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const catOf = (id) => cats.find((c) => c.id === id);
  const body = (p, over) => ({ libelle: p.libelle, type: p.type || "service", prix_unitaire: p.prix_unitaire || 0, tva: p.tva || 0, category_id: p.category_id || null, description: p.description || "", actif: p.actif !== false, ...over });
  const patch = async (p, over) => { try { const { data } = await api.put(`/prestations/${p.id}`, body(p, over)); setItems((prev) => prev.map((x) => x.id === data.id ? data : x)); toast.success("Enregistré automatiquement"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const add = async () => { const { data } = await api.post("/prestations", { libelle: "Nouvelle prestation", type: "service", prix_unitaire: 0, tva: 0 }); setItems((prev) => [data, ...prev]); setEdit(data); };
  const archive = async (p) => { await patch(p, { actif: false }); };
  const del = async (p) => { if (!(await confirm({ message: "Supprimer cette prestation ?" }))) return; try { await api.delete(`/prestations/${p.id}`); load(); toast.success("Supprimé"); } catch { toast.error("Impossible : archivez-la (prestation utilisée)."); } };
  const ttc = (p) => (p.prix_unitaire || 0) * (1 + (p.tva || 0) / 100);
  const filtered = items.filter((p) => filter === "toutes" ? true : filter === "sans" ? !p.category_id : p.category_id === filter);
  const groups = [...cats.map((c) => ({ id: c.id, nom: c.nom, couleur: c.couleur, items: filtered.filter((p) => p.category_id === c.id) })), { id: "sans", nom: "Sans catégorie", couleur: "#b3b7c1", items: filtered.filter((p) => !p.category_id) }].filter((g) => g.items.length);
  const COLS = "56px 2.2fr 1.1fr 1fr 1fr 60px 1fr 1fr 30px";
  return (
    <div style={{ paddingLeft: 0 }}>
      <div className="flex items-center gap-3 mb-3">
        <p className="flex-1 text-[13px]" style={{ color: "#6b7180" }}>Produits et services réutilisables en facturation.</p>
        <button data-testid="prestation-add" onClick={add} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12.5px] font-semibold" style={{ border: "1px solid #e3e3de", background: "#fff", color: "var(--ink)" }}><Plus className="w-3.5 h-3.5" />Prestation</button>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {[["toutes", "Toutes", null], ...cats.map((c) => [c.id, c.nom, c.couleur]), ["sans", "Sans catégorie", "#b3b7c1"]].map(([k, l, col]) => (
          <button key={k} data-testid={`cat-filter-${k}`} onClick={() => setFilter(k)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px]" style={filter === k ? { background: "var(--ink)", color: "#fff" } : { background: "var(--bg-warm)", color: "var(--text-2)" }}>
            {col && <span style={{ width: 8, height: 8, borderRadius: 99, background: col, display: "inline-block" }} />}{l}
          </button>
        ))}
      </div>
      <div className="bg-white overflow-hidden" style={{ border: "1px solid #ebebe7", borderRadius: 12 }}>
        <div className="grid items-center" style={{ gridTemplateColumns: COLS, gap: 12, padding: "10px 18px", background: "#fbfbf9", borderBottom: "1px solid #f0f0ec", fontSize: 11.5, color: "#8a8f9c" }}>
          <span>Réf.</span><span>Libellé</span><span>Catégorie</span><span>Type</span><span className="text-right">Prix HT</span><span className="text-right">TVA</span><span className="text-right">Prix TTC</span><span className="text-right">Utilisée</span><span />
        </div>
        {groups.map((g) => (
          <div key={g.id}>
            <div className="flex items-center gap-2" style={{ background: "var(--table-head, #fbfbf9)", padding: "6px 18px", borderBottom: "1px solid #f0f0ec" }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: g.couleur, display: "inline-block" }} />
              <span className="text-[11.5px] font-semibold" style={{ color: "var(--ink)" }}>{g.nom}</span>
              <span className="mono text-[11px]" style={{ color: "var(--muted-2)" }}>{g.items.length}</span>
            </div>
            {g.items.map((p) => (
              <div key={p.id} data-testid={`prest-row-${p.id}`} onClick={() => setEdit(p)} className="grid items-center cursor-pointer" style={{ gridTemplateColumns: COLS, gap: 12, padding: "11px 18px", borderBottom: "1px solid #f3f3ef", fontSize: 13 }}>
                <span className="mono text-[12px]" style={{ color: "#6b7180" }}>{p.reference}</span>
                <span className="font-semibold truncate" style={{ color: "var(--ink)" }}>{p.libelle}{p.actif === false ? " (archivée)" : ""}</span>
                <span onClick={(e) => e.stopPropagation()}>
                  <select data-testid={`prest-cat-${p.id}`} value={p.category_id || ""} onChange={(e) => patch(p, { category_id: e.target.value || null })} className="text-[12px] rounded-full px-2 py-0.5" style={{ border: "1px solid #e3e3de", background: "#fff" }}>
                    <option value="">Sans catégorie</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </span>
                <span className="inline-flex items-center gap-1.5" style={{ color: "#8a8f9c" }}>{p.type === "produit" ? <Package className="w-3.5 h-3.5" /> : <Wrench className="w-3.5 h-3.5" />}{p.type === "produit" ? "Produit" : "Service"}</span>
                <span className="text-right mono">{eur(p.prix_unitaire)}</span>
                <span className="text-right" style={{ color: "#8a8f9c" }}>{p.tva || 0} %</span>
                <span className="text-right font-semibold mono">{eur(ttc(p))}</span>
                <span className="text-right" style={{ color: "#6b7180" }}>{p.utilisee || 0} fois</span>
                <span className="text-right" onClick={(e) => e.stopPropagation()}>
                  <button data-testid={`prest-archive-${p.id}`} onClick={() => archive(p)} className="text-slate-300 hover:text-slate-600" title="Archiver"><Archive className="w-4 h-4" /></button>
                </span>
              </div>
            ))}
          </div>
        ))}
        {groups.length === 0 && <div className="p-8 text-center text-[13px]" style={{ color: "var(--muted-2)" }}>Aucune prestation. Ajoutez-en une avec « + Prestation ».</div>}
      </div>
      {edit && <PrestationEditPanel prestation={edit} cats={cats} onClose={() => setEdit(null)} onSaved={(d) => { setItems((prev) => prev.map((x) => x.id === d.id ? d : x)); }} />}
    </div>
  );
}

function PrestationEditPanel({ prestation, cats, onClose, onSaved }) {
  const [p, setP] = useState(prestation);
  useEffect(() => setP(prestation), [prestation]);
  const save = async (over) => { const np = { ...p, ...over }; setP(np); try { const { data } = await api.put(`/prestations/${np.id}`, { libelle: np.libelle, type: np.type || "service", prix_unitaire: np.prix_unitaire || 0, tva: np.tva || 0, category_id: np.category_id || null, description: np.description || "", actif: np.actif !== false }); onSaved(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const ttc = (p.prix_unitaire || 0) * (1 + (p.tva || 0) / 100);
  const [ttcInput, setTtcInput] = useState(ttc.toFixed(2));
  useEffect(() => { setTtcInput(((p.prix_unitaire || 0) * (1 + (p.tva || 0) / 100)).toFixed(2)); }, [p.prix_unitaire, p.tva]);
  useEffect(() => { const onKey = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, [onClose]);
  return (
    <div className="fixed inset-y-0 right-0 z-40 bg-white flex flex-col sx-fade-in" style={{ width: 420, boxShadow: "var(--sh-float)", padding: 20 }} data-testid="prestation-edit">
      <div className="flex items-center justify-between mb-3"><h2 className="font-bold text-[18px]" style={{ color: "var(--ink)" }}>Édition prestation</h2><button onClick={onClose} className="sx-icon-btn" aria-label="Fermer"><X className="w-4 h-4" /></button></div>
      <div className="space-y-3 overflow-y-auto">
        <F l="Référence"><input value={p.reference || ""} disabled className="w-full sx-input opacity-60" /></F>
        <F l="Libellé"><input data-testid="prest-edit-libelle" value={p.libelle || ""} onChange={(e) => setP({ ...p, libelle: e.target.value })} onBlur={() => save({})} className="w-full sx-input" /></F>
        <F l="Catégorie"><select data-testid="prest-edit-cat" value={p.category_id || ""} onChange={(e) => save({ category_id: e.target.value || null })} className="w-full sx-input"><option value="">Sans catégorie</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}</select></F>
        <F l="Type"><select value={p.type || "service"} onChange={(e) => save({ type: e.target.value })} className="w-full sx-input"><option value="service">Service</option><option value="produit">Produit</option></select></F>
        <div className="grid grid-cols-2 gap-3">
          <F l="Prix HT"><input type="number" value={p.prix_unitaire || 0} onChange={(e) => setP({ ...p, prix_unitaire: Number(e.target.value) })} onBlur={() => save({})} className="w-full sx-input" /></F>
          <F l="TVA (%)"><input type="number" value={p.tva || 0} onChange={(e) => setP({ ...p, tva: Number(e.target.value) })} onBlur={() => save({})} className="w-full sx-input" /></F>
        </div>
        <F l="Prix TTC"><input type="number" step="0.01" data-testid="prest-edit-ttc" value={ttcInput} onChange={(e) => setTtcInput(e.target.value)} onBlur={() => { const t = Number(ttcInput) || 0; const ht = Math.round((t / (1 + (p.tva || 0) / 100)) * 100) / 100; save({ prix_unitaire: ht }); }} className="w-full sx-input mono" /></F>
        <F l="Description"><textarea rows={3} value={p.description || ""} onChange={(e) => setP({ ...p, description: e.target.value })} onBlur={() => save({})} className="w-full sx-input" style={{ height: "auto" }} /></F>
      </div>
    </div>
  );
}
const F = ({ l, children }) => (<div><label className="text-[12.5px] font-medium block mb-1" style={{ color: "#3b4150" }}>{l}</label>{children}</div>);

function CategoriesPanel() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [nom, setNom] = useState("");
  const [editId, setEditId] = useState(null);
  const [editNom, setEditNom] = useState("");
  const load = () => api.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!nom.trim()) return; await api.post("/categories", { nom, couleur: CAT_COLORS[items.length % CAT_COLORS.length] }); setNom(""); load(); toast.success("Catégorie créée"); };
  const setColor = async (c, couleur) => { await api.put(`/categories/${c.id}`, { nom: c.nom, couleur, ordre: c.ordre, archivee: c.archivee }); load(); };
  const saveNom = async (c) => { if (!editNom.trim()) return; await api.put(`/categories/${c.id}`, { nom: editNom, couleur: c.couleur, ordre: c.ordre, archivee: c.archivee }); setEditId(null); load(); toast.success("Modifié"); };
  const archive = async (c) => { await api.put(`/categories/${c.id}`, { nom: c.nom, couleur: c.couleur, ordre: c.ordre, archivee: !c.archivee }); load(); };
  const del = async (c) => { if (!(await confirm({ message: "Supprimer cette catégorie ?" }))) return; try { await api.delete(`/categories/${c.id}`); load(); toast.success("Supprimée"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <Section title="Catégories de prestation">
      <p className="text-[13px] mb-4" style={{ color: "#6b7180" }}>Organisez vos prestations en catégories pour piloter le CA par catégorie.</p>
      <div className="space-y-2 mb-4">
        {items.length === 0 && <div className="text-sm text-slate-400">Aucune catégorie.</div>}
        {items.map((c) => (
          <div key={c.id} data-testid={`cat-row-${c.id}`} className="flex items-center gap-3 py-2 px-3 border border-slate-100 rounded-lg text-sm">
            <span className="inline-flex gap-1">
              {CAT_COLORS.map((col) => <button key={col} onClick={() => setColor(c, col)} aria-label={`Couleur ${col}`} style={{ width: 12, height: 12, borderRadius: 99, background: col, outline: c.couleur === col ? "2px solid var(--ink)" : "none", outlineOffset: 1 }} />)}
            </span>
            {editId === c.id
              ? <input data-testid={`cat-edit-${c.id}`} value={editNom} onChange={(e) => setEditNom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveNom(c); if (e.key === "Escape") setEditId(null); }} autoFocus className="flex-1 px-2 py-1 rounded border border-slate-300" />
              : <button className="flex-1 text-left font-medium" style={{ color: "var(--ink)" }} onClick={() => { setEditId(c.id); setEditNom(c.nom); }}>{c.nom}{c.archivee ? " (archivée)" : ""}</button>}
            <span className="text-[12px]" style={{ color: "var(--muted)" }}>{c.count} prestation{c.count > 1 ? "s" : ""}</span>
            {editId === c.id
              ? <button data-testid={`cat-save-${c.id}`} onClick={() => saveNom(c)} className="text-emerald-600"><Check className="w-4 h-4" /></button>
              : <>
                <button data-testid={`cat-archive-${c.id}`} onClick={() => archive(c)} className="text-slate-400 hover:text-slate-700" title="Archiver"><Archive className="w-4 h-4" /></button>
                <button data-testid={`cat-del-${c.id}`} onClick={() => del(c)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
              </>}
          </div>
        ))}
      </div>
      <div className="flex gap-2 border-t border-slate-100 pt-4">
        <input data-testid="cat-new-input" value={nom} onChange={(e) => setNom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="Nom de la catégorie" className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        <button data-testid="cat-add" onClick={add} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Plus className="w-4 h-4" />Catégorie</button>
      </div>
    </Section>
  );
}
